import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { API_BASE_URL } from '../config/api'

interface Event {
  id: number
  title: string
  subtitle?: string
  date: string
  googledrivelink: string
  location: string
  image_path?: string
  image?: string
  thumbnail_path?: string
  thumbnail?: string
  description: string
  status: 'upcoming' | 'past'
  ticket_url?: string
  end_date?: string
  is_active?: number
}

const Events = () => {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'past'>('upcoming')
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)


  // Extract start date from display date format (for UI display)
  // Handles both formats: "22 March 2026 12:24pm to 22 March 2026 3:30pm" or "22 March 2026 12:24pm"
  const extractStartDateForDisplay = (displayDate: string): string => {
    if (!displayDate) return ''
    
    // If it contains " to ", extract the start part (before " to ")
    if (displayDate.includes(' to ')) {
      return displayDate.split(' to ')[0].trim()
    }
    
    // Otherwise return as is
    return displayDate
  }

  // Parse date string to Date object for sorting
  // Handles formats like: "Dec 2023 1:23", "22 March 2026 12:24pm", "22 March 2026 12:24pm to 22 March 2026 3:30pm"
  const parseDateForSorting = (dateString: string): Date => {
    if (!dateString) return new Date(0) // Return epoch date for invalid dates
    
    // Extract start date if it contains " to "
    let dateToParse = dateString
    if (dateString.includes(' to ')) {
      dateToParse = dateString.split(' to ')[0].trim()
    }
    
    try {
      // Try parsing formats like "Dec 2023 1:23" or "December 2023 1:23"
      // Pattern: Month Year Hour:Minute
      const monthYearTimeMatch = dateToParse.match(/^(\w+)\s+(\d{4})\s+(\d{1,2}):(\d{2})/i)
      if (monthYearTimeMatch) {
        const monthName = monthYearTimeMatch[1]
        const year = parseInt(monthYearTimeMatch[2])
        const hour = parseInt(monthYearTimeMatch[3])
        const minute = parseInt(monthYearTimeMatch[4])
        
        const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 
                          'july', 'august', 'september', 'october', 'november', 'december']
        const monthNamesShort = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
                                'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
        
        const monthIndex = monthNames.findIndex(m => m.toLowerCase() === monthName.toLowerCase())
        const monthIndexShort = monthNamesShort.findIndex(m => m.toLowerCase() === monthName.toLowerCase())
        const month = monthIndex !== -1 ? monthIndex : (monthIndexShort !== -1 ? monthIndexShort : 0)
        
        return new Date(year, month, 1, hour, minute)
      }
      
      // Try parsing formats like "22 March 2026 12:24pm"
      // Pattern: Day Month Year Hour:Minute(am/pm)
      const dayMonthYearTimeMatch = dateToParse.match(/(\d+)\s+(\w+)\s+(\d{4})\s+(\d{1,2}):(\d{2})(am|pm)/i)
      if (dayMonthYearTimeMatch) {
        const day = parseInt(dayMonthYearTimeMatch[1])
        const monthName = dayMonthYearTimeMatch[2]
        const year = parseInt(dayMonthYearTimeMatch[3])
        let hours = parseInt(dayMonthYearTimeMatch[4])
        const minutes = parseInt(dayMonthYearTimeMatch[5])
        const ampm = dayMonthYearTimeMatch[6].toLowerCase()
        
        const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 
                          'july', 'august', 'september', 'october', 'november', 'december']
        const month = monthNames.findIndex(m => m.toLowerCase() === monthName.toLowerCase())
        
        if (ampm === 'pm' && hours !== 12) hours += 12
        if (ampm === 'am' && hours === 12) hours = 0
        
        if (month !== -1) {
          return new Date(year, month, day, hours, minutes)
        }
      }
      
      // Try parsing formats like "March 21, 2026"
      const monthDayYearMatch = dateToParse.match(/(\w+)\s+(\d+),\s+(\d{4})/i)
      if (monthDayYearMatch) {
        const monthName = monthDayYearMatch[1]
        const day = parseInt(monthDayYearMatch[2])
        const year = parseInt(monthDayYearMatch[3])
        
        const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 
                          'july', 'august', 'september', 'october', 'november', 'december']
        const month = monthNames.findIndex(m => m.toLowerCase() === monthName.toLowerCase())
        
        if (month !== -1) {
          return new Date(year, month, day)
        }
      }
      
      // Try standard Date parsing as fallback
      const parsedDate = new Date(dateToParse)
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate
      }
    } catch (e) {
      console.warn('Error parsing date:', dateToParse, e)
    }
    
    // Return epoch date if parsing fails
    return new Date(0)
  }

  // Helper function to get event image URL
  const getEventImageUrl = (event: Event): string => {
    if (event.thumbnail_path) {
      const cleanPath = event.thumbnail_path.replace(/\\/g, '/')
      return `${API_BASE_URL}${cleanPath}`
    }
    if (event.image_path) {
      const cleanPath = event.image_path.replace(/\\/g, '/')
      return `${API_BASE_URL}${cleanPath}`
    }
    return event.image || event.thumbnail || `${import.meta.env.BASE_URL}aashiqui-banner.webp`
  }

  // Calculate status based on end_date
  const calculateStatus = (endDate: string): 'upcoming' | 'past' => {
    if (!endDate) return 'upcoming'
    const end = new Date(endDate)
    const now = new Date()
    return end >= now ? 'upcoming' : 'past'
  }

  // Fetch events
  const fetchEvents = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}event_list.php`)
      const result = await response.json()
      
      if (result.status && result.data) {
        // Recalculate status and filter active events
        const activeEvents = result.data
          .filter((event: Event) => (event.is_active ?? 1) === 1)
          .map((event: Event) => {
            if (event.end_date) {
              return { ...event, status: calculateStatus(event.end_date) }
            }
            return event
          })
        setEvents(activeEvents)
      }
    } catch (err) {
      console.error('Error fetching events:', err)
      // Keep default/empty state on error
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents()
  }, [])

  // Default/fallback events
  const defaultUpcomingEvents: Event[] = [
    {
      id: 1,
      title: 'Aashiqui With Kumar Sanu & Sadhana Sargam',
      subtitle: 'UNFORGETTABLE 90s',
      date: 'March 21, 2026',
      googledrivelink: '',
      location: 'Ovens Auditorium, Charlotte, NC',
      image: `${import.meta.env.BASE_URL}aashiqui-banner.webp`,
      description: 'Experience the magic of 90s Bollywood music with legendary singers Kumar Sanu and Sadhana Sargam in this unforgettable concert.',
      status: 'upcoming',
    }
  ]

  const defaultPastEvents: Event[] = [
    {
      id: 6,
      title: 'Sau Saal Pehle - A Tribute to Mohammed Rafi',
      subtitle: 'By Sonu Nigam',
      date: 'June-July 2023',
      googledrivelink: '',
      location: 'North America Tour',
      image: `${import.meta.env.BASE_URL}New-flyer-with-sponsor.webp`,
      description: 'Sonu Nigam paid a beautiful tribute to the legendary Mohammed Rafi in this spectacular tour.',
      status: 'past',
    },
    {
      id: 7,
      title: 'Niladri Kumar\'s Dream India',
      subtitle: 'The Sitar Odyssey Live',
      date: '2023',
      googledrivelink: '',
      location: 'USA - Canada Tour',
      image: `${import.meta.env.BASE_URL}superhit.webp`,
      description: 'A mesmerizing journey through Indian classical music that captivated audiences across North America.',
      status: 'past',
    },
    {
      id: 8,
      title: '100 Years of Raj Kapoor',
      subtitle: 'Celebrating a Legend',
      date: '2022',
      googledrivelink: '',
      location: 'North America',
      image: `${import.meta.env.BASE_URL}aashiqui-banner.webp`,
      description: 'A grand celebration honoring the centenary of the legendary Raj Kapoor with star-studded performances.',
      status: 'past',
    },
  ]

  // Separate upcoming and past events, then sort by date in descending order
  const upcomingEvents = events.length > 0
    ? events
        .filter(event => event.status === 'upcoming')
        .map(event => ({
          ...event,
          image: getEventImageUrl(event),
        }))
        .sort((a, b) => {
          const dateA = parseDateForSorting(a.date)
          const dateB = parseDateForSorting(b.date)
          return dateB.getTime() - dateA.getTime() // Descending order (newest first)
        })
    : defaultUpcomingEvents.sort((a, b) => {
        const dateA = parseDateForSorting(a.date)
        const dateB = parseDateForSorting(b.date)
        return dateB.getTime() - dateA.getTime() // Descending order (newest first)
      })

  const pastEvents = events.length > 0
    ? events
        .filter(event => event.status === 'past')
        .map(event => ({
          ...event,
          image: getEventImageUrl(event),
        }))
        .sort((a, b) => {
          const dateA = parseDateForSorting(a.date)
          const dateB = parseDateForSorting(b.date)
          return dateB.getTime() - dateA.getTime() // Descending order (newest first)
        })
    : defaultPastEvents.sort((a, b) => {
        const dateA = parseDateForSorting(a.date)
        const dateB = parseDateForSorting(b.date)
        return dateB.getTime() - dateA.getTime() // Descending order (newest first)
      })

  // Combine and sort all events in descending order
  const allEvents = [...upcomingEvents, ...pastEvents].sort((a, b) => {
    const dateA = parseDateForSorting(a.date)
    const dateB = parseDateForSorting(b.date)
    return dateB.getTime() - dateA.getTime() // Descending order (newest first)
  })

  const filteredEvents = filter === 'all' 
    ? allEvents 
    : filter === 'upcoming' 
    ? upcomingEvents 
    : pastEvents

  return (
    <div className="w-full min-h-screen bg-gray-900 py-8 md:py-12">
      {/* Hero Section */}
      {/* <section className="bg-gradient-to-br from-gray-900 via-primary-dark/20 to-gray-900 py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/10 via-transparent to-primary-light/10 animate-gradient-bg"></div>
          <div className="floating-shapes"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-primary-light text-sm md:text-base font-semibold uppercase tracking-wider">
              Our Events
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Unforgettable Experiences
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
              Join us for spectacular Bollywood concerts and cultural events featuring the biggest stars from India.
            </p>
          </div>
        </div>
      </section> */}

      {/* Filter Buttons */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <button
            onClick={() => setFilter('upcoming')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 min-h-[44px] ${
              filter === 'upcoming'
                ? 'bg-primary-light text-white shadow-lg'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            Upcoming Events
          </button>
          <button
            onClick={() => setFilter('past')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 min-h-[44px] ${
              filter === 'past'
                ? 'bg-primary-light text-white shadow-lg'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            Past Events
          </button>
            <button
            onClick={() => setFilter('all')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 min-h-[44px] ${
              filter === 'all'
                ? 'bg-primary-light text-white shadow-lg'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            All Events
          </button>
        </div>

        {/* Events Grid */}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-light mx-auto mb-4"></div>
            <p className="text-gray-400 text-lg">Loading events...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {filteredEvents.map((event) => (
            
            <div
                key={event.id}
                onClick={() => {
                  navigate(`/events/${event.id}`)
                }}
                className="bg-gray-800 rounded-xl overflow-hidden transition-all duration-300 group shadow-lg cursor-pointer hover:bg-gray-750 hover:shadow-2xl hover:-translate-y-2"
              >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={event.image}
                  alt={event.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4 bg-primary-light text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-lg">
                  {extractStartDateForDisplay(event.date)}
                </div>
                {filter === 'upcoming' && (
                  <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase">
                    Upcoming
                  </div>
                )}
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-white mb-2 line-clamp-2 group-hover:text-primary-light transition-colors">
                  {event.title}
                </h3>
                {event.subtitle && (
                  <p className="text-primary-light text-sm font-semibold mb-3">
                    {event.subtitle}
                  </p>
                )}
                <div className="flex items-center text-gray-400 text-sm mb-4">
                  <svg
                    className="w-5 h-5 mr-2 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="line-clamp-1">{event.location}</span>
                </div>
                <p className="text-gray-300 text-sm mb-6 line-clamp-3">
                  {event.description}
                </p>
                {filter === 'upcoming' && (
                  <a
                    onClick={(e) => e.stopPropagation()}
                    href={event.ticket_url || "https://www.ticketmaster.com/event/2D006375A9CAB6E2"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-primary-light hover:bg-primary-dark text-white px-4 py-3 rounded-lg font-semibold transition-colors text-sm md:text-base min-h-[44px] inline-block text-center"
                  >
                    Buy Tickets
                  </a>
                )}
              </div>
            </div>
          ))}
          </div>
        )}

        {!loading && filteredEvents.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-400 text-lg">No events found in this category.</p>
          </div>
        )}

        {/* Back to Home */}
        <div className="text-center mt-12">
          <Link
            to="/"
            className="inline-flex items-center text-primary-light hover:text-primary-dark font-semibold transition-colors"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Home
          </Link>
        </div>
      </section>
    </div>
  )
}

export default Events

